import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, Clock, CheckCircle, XCircle, Calendar, Search, Filter, Trash2, Send, Plus, Users } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import CountUp from 'react-countup';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];

export default function NotificationHistory() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Send Notification Modal state
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  // Subscriptions Modal state
  const [isSubsModalOpen, setIsSubsModalOpen] = useState(false);
  const [subsStats, setSubsStats] = useState(null);
  const [loadingSubsStats, setLoadingSubsStats] = useState(false);
  const [sendFormData, setSendFormData] = useState({
    title: '', body: '', icon: '', url: '/', targetingType: 'all', targetIds: []
  });
  const [sending, setSending] = useState(false);
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [modalUsers, setModalUsers] = useState([]);
  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'yonetici', label: 'Yönetici' },
    { value: 'proje_yoneticisi', label: 'Proje Yöneticisi' },
    { value: 'takım_lideri', label: 'Takım Lideri' },
    { value: 'takım_üyesi', label: 'Takım Üyesi' }
  ];

  useEffect(() => {
    fetchStats();
    fetchHistory();
  }, [page, filterType]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/push/history/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Stats fetch error:', error);
      toast.error('İstatistikler yüklenemedi');
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        ...(search && { search }),
        ...(filterType !== 'all' && { notification_type: filterType })
      };
      
      const response = await axios.get(`${API}/push/history`, { params });
      setHistory(response.data.history);
      setTotalPages(response.data.totalPages);
      setFilteredTotal(response.data.total);
    } catch (error) {
      console.error('History fetch error:', error);
      toast.error('Geçmiş yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchHistory();
  };

  const handleDeleteFailed = async () => {
    // Onay iste
    const confirmed = window.confirm(
      'Tamamen başarısız (hiç teslim edilememiş) bildirim kayıtlarını silmek istediğinizden emin misiniz?\n\n' +
      'Not: Kısmen başarılı kayıtlar silinmeyecektir.\n\n' +
      'Bu işlem geri alınamaz!'
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await axios.delete(`${API}/push/history/failed`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setTimeout(() => {
          fetchHistory();
          fetchStats();
        }, 300);
      }
    } catch (error) {
      console.error('❌ Silme hatası:', error);
      toast.error(error.response?.data?.detail || 'Kayıtlar silinemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId, title) => {
    // Onay iste
    const confirmed = window.confirm(
      `"${title}" başlıklı bildirimi silmek istediğinizden emin misiniz?\n\n` +
      'Bu işlem geri alınamaz!'
    );
    
    if (!confirmed) return;

    try {
      const response = await axios.delete(`${API}/push/history/${notificationId}`);
      
      if (response.data.success) {
        toast.success('Bildirim başarıyla silindi');
        setTimeout(() => {
          fetchHistory();
          fetchStats();
        }, 300);
      }
    } catch (error) {
      console.error('❌ Silme hatası:', error);
      toast.error(error.response?.data?.detail || 'Bildirim silinemedi');
    }
  };

  // --- Subscriptions Modal Logic ---
  const openSubsModal = async () => {
    setIsSubsModalOpen(true);
    setLoadingSubsStats(true);
    try {
      const response = await axios.get(`${API}/push/subscriptions/stats`);
      setSubsStats(response.data);
    } catch (error) {
      console.error('Abonelik istatistikleri yüklenemedi:', error);
      toast.error('Abonelik istatistikleri yüklenemedi');
    } finally {
      setLoadingSubsStats(false);
    }
  };

  // --- Send Notification Modal Logic ---
  const loadTargetingData = async () => {
    try {
      const [teamsRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`)
      ]);
      setTeams(teamsRes.data);
      setProjects(projectsRes.data);
      setModalUsers(usersRes.data);
    } catch (error) {
      console.error('Hedefleme verileri yüklenemedi:', error);
    }
  };

  const openSendModal = () => {
    loadTargetingData();
    setSendFormData({ title: '', body: '', icon: '', url: '/', targetingType: 'all', targetIds: [] });
    setIsSendModalOpen(true);
  };

  const handleSendInputChange = (e) => {
    const { name, value } = e.target;
    setSendFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'targetingType' && { targetIds: [] })
    }));
  };

  const handleCheckboxToggle = (id) => {
    setSendFormData(prev => ({
      ...prev,
      targetIds: prev.targetIds.includes(id)
        ? prev.targetIds.filter(item => item !== id)
        : [...prev.targetIds, id]
    }));
  };

  const handleSelectAll = (items) => {
    const allIds = items.map(item => item.id);
    const allSelected = allIds.every(id => sendFormData.targetIds.includes(id));
    setSendFormData(prev => ({ ...prev, targetIds: allSelected ? [] : allIds }));
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!sendFormData.title.trim() || !sendFormData.body.trim()) {
      toast.error('Başlık ve mesaj zorunludur');
      return;
    }
    setSending(true);
    try {
      let targetIds = null;
      if (sendFormData.targetingType !== 'all' && sendFormData.targetIds.length > 0) {
        targetIds = sendFormData.targetIds;
      }
      const payload = {
        title: sendFormData.title,
        body: sendFormData.body,
        icon: sendFormData.icon || '/onxflow_logo.png',
        url: sendFormData.url || '/',
        targetingType: sendFormData.targetingType,
        targetIds
      };
      const response = await axios.post(`${API}/push/send`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(`Bildirim başarıyla gönderildi! ${response.data.successful} kullanıcıya ulaştı.`, {
        description: response.data.failed > 0 ? `${response.data.failed} kullanıcıya gönderilemedi.` : undefined
      });
      setIsSendModalOpen(false);
      setPage(1);
      setTimeout(() => {
        fetchHistory();
        fetchStats();
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bildirim gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Event türü çevirileri - detaylı açıklamalar
  const eventTypeLabels = {
    'new_task': 'Yeni Görev Atandı',
    'comment': 'Yeni Yorum Eklendi',
    'file_upload': 'Dosya Yüklendi',
    'revision': 'Revizyon Talebi',
    'review': 'İncelemeye Gönderildi',
    'workflow_progress': 'İş Akışı İlerledi',
    'task_update': 'Görev Güncellendi',
    'task_delete': 'Görev Silindi',
    'project_created': 'Yeni Proje Oluşturuldu'
  };
  
  // Event türü kısa açıklamalar (tablo için)
  const eventTypeShortLabels = {
    'new_task': 'Yeni Görev',
    'comment': 'Yorum',
    'file_upload': 'Dosya',
    'revision': 'Revizyon',
    'review': 'İnceleme',
    'workflow_progress': 'İş Akışı',
    'task_update': 'Güncelleme',
    'task_delete': 'Silme',
    'project_created': 'Proje'
  };

  if (!stats) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            Bildirim Geçmişi & İstatistikler
          </h1>
          <p className="text-gray-600">
            Gönderilen bildirimlerin detaylı analizi ve geçmişi
          </p>
        </div>
        {user && ['admin', 'yonetici'].includes(user.role) && (
          <div className="flex gap-2 self-start sm:self-auto">
            <Button
              onClick={openSubsModal}
              data-testid="subscriptions-btn"
              variant="outline"
              className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <Users className="w-4 h-4" />
              Aktif Abonelikler
            </Button>
            <Button
              onClick={openSendModal}
              data-testid="send-notification-btn"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg gap-2"
            >
              <Send className="w-4 h-4" />
              Bildirim Gönder
            </Button>
          </div>
        )}
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="glass rounded-2xl p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Toplam Gönderim</p>
              <p className="text-3xl font-bold text-gray-900"><CountUp end={stats.totalSent} duration={1.5} separator="." /></p>
            </div>
            <TrendingUp className="w-10 h-10 text-blue-500 opacity-50" />
          </div>
        </div>

        <div className="glass rounded-2xl p-6 border-l-4 border-cyan-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Bu Ay</p>
              <p className="text-3xl font-bold text-gray-900"><CountUp end={stats.thisMonth} duration={1.5} separator="." /></p>
            </div>
            <Calendar className="w-10 h-10 text-cyan-500 opacity-50" />
          </div>
        </div>

        <div className="glass rounded-2xl p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Bu Hafta</p>
              <p className="text-3xl font-bold text-gray-900"><CountUp end={stats.thisWeek} duration={1.5} separator="." /></p>
            </div>
            <Clock className="w-10 h-10 text-purple-500 opacity-50" />
          </div>
        </div>

        <div className="glass rounded-2xl p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Başarı Oranı</p>
              <p className="text-3xl font-bold text-gray-900"><CountUp end={parseFloat(stats.successRate)} duration={1.5} decimals={1} />%</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.successfulRecords || (stats.totalSent - (stats.totalCompletelyFailed || 0))} başarılı / {stats.totalCompletelyFailed || 0} tamamen başarısız
              </p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Son 30 Gün Trend Grafiği - 2 kolon genişliğinde */}
        <div className="glass rounded-2xl p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Son 30 Gün Trendi
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats.dailyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="_id" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  // YYYY-MM-DD → DD-MM formatına çevir (Türkiye formatı)
                  const parts = value.split('-');
                  return parts.length === 3 ? `${parts[2]}-${parts[1]}` : value;
                }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
                labelFormatter={(value) => {
                  // YYYY-MM-DD → DD/MM/YYYY formatına çevir
                  const parts = value.split('-');
                  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
                }}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                name="Bildirim Sayısı"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Olay Türü Dağılımı */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-cyan-600" />
            Olay Türü Dağılımı
          </h3>
          {stats.byEvent && stats.byEvent.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.byEvent}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="_id" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => eventTypeLabels[value] || value}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => eventTypeLabels[value] || value}
                />
                <Bar dataKey="count" fill="#06b6d4" name="Bildirim Sayısı" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
              <BarChart3 className="w-16 h-16 mb-3 opacity-30" />
              <p className="text-sm">Henüz otomatik bildirim verisi yok</p>
              <p className="text-xs mt-1">Sistem otomatik bildirimler gönderdiğinde burada görünecek</p>
            </div>
          )}
        </div>

        {/* Saatlik Dağılım */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Saatlik Dağılım
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.hourlyDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="_id" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}:00`}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
                labelFormatter={(value) => `Saat: ${value}:00`}
              />
              <Bar dataKey="count" fill="#f59e0b" name="Bildirim Sayısı" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Bildirim Türü Dağılımı - Geçici olarak gizlendi */}
        {/* <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5 text-purple-600" />
            Bildirim Türü Dağılımı
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={stats.byType.map(item => ({
                  name: item._id === 'manual' ? 'Manuel' : 'Otomatik',
                  value: item.count
                }))}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {stats.byType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div> */}
      </div>

      {/* Bildirim Geçmişi Tablosu */}
      <div className="glass rounded-2xl p-6">
        <div className="flex flex-col gap-4 mb-6">
          {/* Üst satır: Başlık + Badge + Filtreler */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Bildirim Geçmişi
              </h3>
              {/* Toplam/filtrelenmiş bildirim sayısı badge - biraz yukarıda */}
              {(filteredTotal > 0 || stats?.totalSent > 0) && (
                <span className="min-w-[1.75rem] h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md inline-flex items-center justify-center shadow-lg -translate-y-1">
                  {(search || filterType !== 'all' ? filteredTotal : stats?.totalSent || 0).toLocaleString('tr-TR')}
                </span>
              )}
            </div>

            {/* Filtreler */}
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full sm:w-48"
                />
                <Button onClick={handleSearch} size="sm" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tümü</option>
                <option value="manual">Manuel</option>
                <option value="automatic">Otomatik</option>
              </select>
            </div>
          </div>
          
          {/* Alt satır: Admin butonu (hem mobilde hem desktopda ayrı satırda) */}
          {user?.role === 'admin' && (
            <div className="flex items-center gap-3">
              <Button 
                onClick={handleDeleteFailed}
                size="sm" 
                variant="outline"
                disabled={!stats?.totalCompletelyFailed || stats.totalCompletelyFailed === 0}
                className={`border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 ${
                  (!stats?.totalCompletelyFailed || stats.totalCompletelyFailed === 0) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Tamamen Başarısız Kayıtları Sil
              </Button>
              {/* Tamamen başarısız kayıt sayısı badge */}
              {stats?.totalCompletelyFailed > 0 && (
                <span className="min-w-[1.75rem] h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md inline-flex items-center justify-center shadow-lg -translate-y-1">
                  {stats.totalCompletelyFailed.toLocaleString('tr-TR')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tablo */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>Henüz bildirim geçmişi yok</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase whitespace-nowrap">Tarih/Saat</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase w-[200px]">Başlık</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase w-[250px]">Mesaj</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase w-[150px]">Gönderen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase whitespace-nowrap">Tür</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase whitespace-nowrap">Hedef</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase whitespace-nowrap">Durum</th>
                    {user?.role === 'admin' && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase whitespace-nowrap">İşlem</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {history.map((item) => (
                    <tr 
                      key={item.id} 
                      className="hover:bg-gray-50 transition-colors group relative cursor-pointer"
                      onClick={() => {
                        // Mobil ve desktop için toast bildirim
                        toast.info(
                          <div className="text-sm">
                            <div className="font-semibold text-blue-600 mb-1">
                              {eventTypeLabels[item.eventType] || 'Bildirim'}
                            </div>
                            <div><strong>Başlık:</strong> {item.title}</div>
                            <div><strong>Mesaj:</strong> {item.body}</div>
                            <div><strong>Gönderen:</strong> {item.sentByName}</div>
                            <div><strong>Tarih:</strong> {formatDate(item.createdAt)}</div>
                          </div>,
                          { duration: 4000 }
                        );
                      }}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-[200px] truncate">
                        {item.title}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-[250px] truncate">
                        {item.body}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-[150px] truncate">
                        {item.sentByName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                          item.notificationType === 'manual' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {item.notificationType === 'manual' ? 'Manuel' : 'Otomatik'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {item.targetUserIds.length} kullanıcı
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle className="w-3 h-3" />
                            {item.successCount}
                          </span>
                          {item.failCount > 0 && (
                            <span className="flex items-center gap-1 text-xs text-red-600">
                              <XCircle className="w-3 h-3" />
                              {item.failCount}
                            </span>
                          )}
                        </div>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Satır tıklamasını engelle
                              handleDeleteNotification(item.id, item.title);
                            }}
                            className="inline-flex items-center justify-center p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Bildirimi Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Sayfa {page} / {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="outline"
                    size="sm"
                  >
                    Önceki
                  </Button>
                  <Button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Sonraki
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bildirim Gönder Modal */}
      <Dialog open={isSendModalOpen} onOpenChange={setIsSendModalOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="send-notification-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Send className="w-4 h-4 text-white" />
              </div>
              Push Bildirim Gönder
            </DialogTitle>
            <DialogDescription>
              Kullanıcılara anında bildirim gönderin
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSendNotification} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="modal-title">Başlık <span className="text-red-500">*</span></Label>
              <Input
                id="modal-title"
                name="title"
                value={sendFormData.title}
                onChange={handleSendInputChange}
                placeholder="Bildirim başlığı (maks. 100 karakter)"
                maxLength={100}
                required
                className="mt-1"
                data-testid="notification-title-input"
              />
              <p className="text-xs text-gray-500 mt-1">{sendFormData.title.length}/100</p>
            </div>

            <div>
              <Label htmlFor="modal-body">Mesaj <span className="text-red-500">*</span></Label>
              <textarea
                id="modal-body"
                name="body"
                value={sendFormData.body}
                onChange={handleSendInputChange}
                placeholder="Bildirim mesajı (maks. 500 karakter)"
                maxLength={500}
                required
                rows={3}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="notification-body-input"
              />
              <p className="text-xs text-gray-500 mt-1">{sendFormData.body.length}/500</p>
            </div>

            <div>
              <Label htmlFor="modal-url">Tıklama Yönlendirme URL'i</Label>
              <Input
                id="modal-url"
                name="url"
                value={sendFormData.url}
                onChange={handleSendInputChange}
                placeholder="/tasks veya /dashboard"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="modal-targetingType">Hedef Kitle <span className="text-red-500">*</span></Label>
              <select
                id="modal-targetingType"
                name="targetingType"
                value={sendFormData.targetingType}
                onChange={handleSendInputChange}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="notification-target-select"
              >
                <option value="all">Tüm Kullanıcılar</option>
                <option value="role">Rol Bazlı</option>
                <option value="team">Takım Bazlı</option>
                <option value="selected">Seçili Kullanıcılar</option>
              </select>
            </div>

            {/* Role Selection */}
            {sendFormData.targetingType === 'role' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Roller</Label>
                  <button type="button" onClick={() => {
                    const allR = roles.map(r => r.value);
                    const allSel = allR.every(r => sendFormData.targetIds.includes(r));
                    setSendFormData(prev => ({ ...prev, targetIds: allSel ? [] : allR }));
                  }} className="text-xs text-blue-600 hover:underline">
                    {roles.every(r => sendFormData.targetIds.includes(r.value)) ? 'Hiçbirini Seçme' : 'Tümünü Seç'}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-md p-2 space-y-1 max-h-40 overflow-y-auto">
                  {roles.map((role) => (
                    <label key={role.value} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded text-sm">
                      <input type="checkbox" checked={sendFormData.targetIds.includes(role.value)} onChange={() => handleCheckboxToggle(role.value)} className="w-4 h-4 text-blue-600 rounded" />
                      {role.label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">{sendFormData.targetIds.length} rol seçildi</p>
              </div>
            )}

            {/* Team Selection */}
            {sendFormData.targetingType === 'team' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Takımlar</Label>
                  <button type="button" onClick={() => handleSelectAll(teams)} className="text-xs text-blue-600 hover:underline">
                    {teams.length > 0 && teams.every(t => sendFormData.targetIds.includes(t.id)) ? 'Hiçbirini Seçme' : 'Tümünü Seç'}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-md p-2 space-y-1 max-h-40 overflow-y-auto">
                  {teams.length > 0 ? teams.map((team) => (
                    <label key={team.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded text-sm">
                      <input type="checkbox" checked={sendFormData.targetIds.includes(team.id)} onChange={() => handleCheckboxToggle(team.id)} className="w-4 h-4 text-blue-600 rounded" />
                      {team.name}
                    </label>
                  )) : <p className="text-sm text-gray-500 text-center py-2">Takım bulunamadı</p>}
                </div>
                <p className="text-xs text-gray-500 mt-1">{sendFormData.targetIds.length} takım seçildi</p>
              </div>
            )}

            {/* User Selection */}
            {sendFormData.targetingType === 'selected' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Kullanıcılar</Label>
                  <button type="button" onClick={() => handleSelectAll(modalUsers)} className="text-xs text-blue-600 hover:underline">
                    {modalUsers.length > 0 && modalUsers.every(u => sendFormData.targetIds.includes(u.id)) ? 'Hiçbirini Seçme' : 'Tümünü Seç'}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-md p-2 space-y-1 max-h-40 overflow-y-auto">
                  {modalUsers.length > 0 ? modalUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded text-sm">
                      <input type="checkbox" checked={sendFormData.targetIds.includes(u.id)} onChange={() => handleCheckboxToggle(u.id)} className="w-4 h-4 text-blue-600 rounded" />
                      <span className="font-medium">{u.name}</span>
                      <span className="text-xs text-gray-500">({u.email})</span>
                    </label>
                  )) : <p className="text-sm text-gray-500 text-center py-2">Kullanıcı bulunamadı</p>}
                </div>
                <p className="text-xs text-gray-500 mt-1">{sendFormData.targetIds.length} kullanıcı seçildi</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={sending}
              data-testid="send-notification-submit-btn"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg"
            >
              {sending ? 'Gönderiliyor...' : (
                <><Send className="w-4 h-4 mr-2" />Bildirim Gönder</>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Aktif Abonelikler Modal */}
      <Dialog open={isSubsModalOpen} onOpenChange={setIsSubsModalOpen}>
        <DialogContent className="max-w-md" data-testid="subscriptions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              Aktif Abonelikler
            </DialogTitle>
            <DialogDescription>
              Push bildirim abonelik durumu
            </DialogDescription>
          </DialogHeader>

          {loadingSubsStats ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : subsStats ? (
            <div className="space-y-4 mt-2">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{subsStats.activeSubscriptions}</p>
                    <p className="text-sm text-gray-600">Aktif Abonelik</p>
                  </div>
                </div>
              </div>

              {subsStats.byUser && subsStats.byUser.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Kullanıcı Bazlı Dağılım</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {subsStats.byUser.map((item) => (
                      <div key={item.userId} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.userName}</p>
                          <p className="text-xs text-gray-500 truncate">{item.userEmail}</p>
                        </div>
                        <span className="ml-2 text-sm font-semibold text-blue-600 flex-shrink-0">{item.count} cihaz</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span>Sistem aktif ve çalışıyor</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Abonelik verisi bulunamadı</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
